import React, { FunctionComponent, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { Placement } from '@popperjs/core';

import '../../tooltip/tooltip.css';
import { ReactComponent as FilterIcon } from '@components/icons/filter.svg';
import { ReactComponent as FilterOutline } from '@components/icons/filterOutline.svg';
import classNames from 'classnames';
import { IFilterResult } from '@components/filters/filterLib';
import { FilterContext } from './context';

export type FilterFormProps = {
  close: () => void
};

export interface IProps {
  FilterForm: FunctionComponent<FilterFormProps>,
  isOpen?: boolean,
  onToggle: () => void,
  onClose: (referenceElement: HTMLElement | null) => void,
  className?: string,
  columnId: string,

  placement?: Placement,
  offset?: number,
  popoverPortalSelector?: string // needs to be set if you want to use createPortal and set different location in the DOM for children, to ensure that content is rendered above other elements
}

export const FilterDialog: FunctionComponent<IProps> = (props) => {
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [arrowElement, setArrowElement] = useState(null);

  function handleBlur(e: React.FocusEvent) {
    const next = e.relatedTarget;

    if (
      next &&
      (referenceElement?.contains(next) ||
        !referenceElement ||
        popperElement?.contains(next) ||
        !popperElement)
    ) {
      return;
    }

    props.onClose(referenceElement);
  }

  const { getColumnFromFilterGroup, filters, filterOptions: { availableFilters } } = React.useContext(FilterContext);

  const filterIsAvailable = Object.keys(availableFilters).find(k => getColumnFromFilterGroup(k) === props.columnId);

  const filteredColumns = Object.entries(filters).reduce<string[]>((acc, [filterGroup, values]) => {
    if (Array.isArray(values) && values.length > 0) {
      const columnId = getColumnFromFilterGroup(filterGroup);
      if (!acc.includes(columnId)) {
        acc.push(columnId);
      }
    }
    return acc;
  }, []);

  const isFiltered = !!filteredColumns.find(f => f === props.columnId);

  const placement = props.placement || 'bottom';

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: [
      { name: 'arrow', options: { element: arrowElement } },
      { name: 'offset', options: { offset: [0, 7] } },
      {
        name: 'preventOverflow',
        options: {
          tether: false
        },
      },
    ],
  });

  let popover = (
    <div
      ref={setPopperElement}
      className={`filter-dialog_popover-element ${placement}`}
      style={styles.popper}
      {...attributes.popper}
      data-id="filter-dialog-popover"
      onClick={(e) => e.stopPropagation()}
      onBlur={handleBlur}
      tabIndex={-1}
    >
      <div ref={setArrowElement} style={styles.arrow} className="filter-dialog-arrow" />
      <props.FilterForm close={() => props.onClose(referenceElement)} />
    </div>
  );

  if (props.popoverPortalSelector) {
    const portalTarget = document.querySelector(props.popoverPortalSelector);
    if (portalTarget) {
      popover = createPortal(popover, portalTarget);
    }
  }

  const Icon = isFiltered ? FilterIcon : FilterOutline;

  if (!filterIsAvailable) {
    return <></>;
  }

  return (
    <>
      <span
        ref={setReferenceElement}
        className={classNames(props.className, 'filter-dialog_reference-element')}
        data-open={props.isOpen}
        data-filtered={isFiltered}
        data-id="filter-dialog-reference"
        onClick={(e) => {
          e.stopPropagation();
          props.onToggle();
        }}
        tabIndex={0}
        onKeyUp={e => {
          if (e.key === 'Enter') {
            e.stopPropagation();
            props.onToggle();
          }
        }}
        onBlur={handleBlur}
      >
        <Icon className='filter-dialog_icon' data-id="filter-dialog-icon" />
      </span>
      {props.isOpen && popover}
    </>
  );
};
